import { inject } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { EmployeeService } from '@shared/services';
import {
  BeneficiaryActions,
  BusinessAcccountActions,
  EmployeeActions,
  ExceptionManagementActions,
  FinancialAcccountActions,
  TransactionsActions,
} from '@store';
import { map, catchError, of, mergeMap } from 'rxjs';
import { TeamMembersActions } from './team-members.actions';
import { teamMembersFeature } from './team-members.state';
import { compact } from 'lodash-es';

export const loadReferencedTeamMembers$ = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(
        BusinessAcccountActions.loadBusinessAccountDetailsSuccess,
        EmployeeActions.loadEmployeeDetailsSuccess,
        FinancialAcccountActions.loadFinancialAccountDetailsSuccess,
        BusinessAcccountActions.loadServiceAccountDetailsSuccess,
        BeneficiaryActions.loadBeneficiaryDetailsSuccess,
        ExceptionManagementActions.loadExceptionDetailsSuccess,
        ExceptionManagementActions.loadExceptionHistorySuccess,
        TransactionsActions.loadTransactionDetailsSuccess,
        TransactionsActions.loadTransactionHistorySuccess,
      ),
      map((action) => {
        let teamMemberIds: string[] = [];
        let businessAccountId = '';
        switch (action.type) {
          case BusinessAcccountActions.loadBusinessAccountDetailsSuccess.type: {
            const businessAccountDetails = action.businessAccount;
            businessAccountId = businessAccountDetails?.id ?? '';
            teamMemberIds = compact([businessAccountDetails?.createdBy?.id, businessAccountDetails?.updatedBy?.id]);
            break;
          }
          case EmployeeActions.loadEmployeeDetailsSuccess.type: {
            const employeeDetails = action.employeeDetails;
            businessAccountId = employeeDetails?.businessAccountId ?? '';
            teamMemberIds = compact([employeeDetails?.createdBy?.id, employeeDetails?.updatedBy?.id]);
            break;
          }
          case FinancialAcccountActions.loadFinancialAccountDetailsSuccess.type: {
            const financialAccountDetails = action.financialAccount;
            businessAccountId = financialAccountDetails?.businessAccountId ?? '';
            teamMemberIds = compact([financialAccountDetails?.createdBy?.id, financialAccountDetails?.updatedBy?.id]);
            break;
          }
          case BusinessAcccountActions.loadServiceAccountDetailsSuccess.type: {
            const serviceAccountDetails = action.serviceAccount;
            businessAccountId = serviceAccountDetails?.parentId ?? '';
            teamMemberIds = compact([serviceAccountDetails?.createdBy?.id, serviceAccountDetails?.updatedBy?.id]);
            break;
          }
          case ExceptionManagementActions.loadExceptionDetailsSuccess.type: {
            const exceptionDetails = action.exceptionDetails;
            businessAccountId = exceptionDetails?.businessAccountId ?? '';
            teamMemberIds = compact([exceptionDetails?.createdBy?.id, exceptionDetails?.updatedBy?.id]);
            break;
          }
          case ExceptionManagementActions.loadExceptionHistorySuccess.type: {
            const exceptionHistoryItems = action.exceptionHistoryItems;
            businessAccountId = action.businessAccountId;
            teamMemberIds = exceptionHistoryItems.map((item) => item.updatedBy.id);
            break;
          }
          case TransactionsActions.loadTransactionDetailsSuccess.type: {
            const transactionDetails = action.transactionDetails;
            businessAccountId = action.transactionDetails?.businessAccountId ?? '';
            teamMemberIds = compact([transactionDetails?.createdBy?.id, transactionDetails?.updatedBy?.id]);
            break;
          }
          case TransactionsActions.loadTransactionHistorySuccess.type: {
            const transactionHistory = action.transactionHistory;
            businessAccountId = action.businessAccountId ?? '';
            teamMemberIds = compact(transactionHistory.map((item) => item.createdBy?.id));
            break;
          }
          case BeneficiaryActions.loadBeneficiaryDetailsSuccess.type: {
            const details = action.beneficiaryDetails;
            businessAccountId = details.parentId ?? '';
            const historyIds = details.verificationStatusHistory?.map((item) => item.updatedBy?.id) ?? [];
            teamMemberIds = compact([
              details.createdBy?.id,
              details.updatedBy?.id,
              details.verificationStatus?.updatedBy?.id,
              ...historyIds,
            ]);
            break;
          }
        }

        const uniqueIds = [...new Set(teamMemberIds.filter(Boolean))];

        return uniqueIds.length && businessAccountId.length
          ? TeamMembersActions.loadTeamMembers({
              ids: uniqueIds,
              businessAccountId: businessAccountId,
            })
          : TeamMembersActions.noTeamMembersToLoad();
      }),
    );
  },
  { functional: true },
);

export const loadTeamMembers$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store), teamMembersService = inject(EmployeeService)) => {
    return actions$.pipe(
      ofType(TeamMembersActions.loadTeamMembers),
      concatLatestFrom(() => store.select(teamMembersFeature.selectEntities)),
      mergeMap(([action, entities]) => {
        const missingIds = action.ids.filter((id) => !entities[id]);

        if (!missingIds.length || !action.businessAccountId.length) {
          return of(TeamMembersActions.noTeamMembersToLoad());
        }

        return teamMembersService.getSubjectsQuickInfo(action.businessAccountId, missingIds).pipe(
          map((teamMembers) => TeamMembersActions.loadTeamMembersSuccess({ teamMembers })),
          catchError((error) => of(TeamMembersActions.loadTeamMembersFailure({ error }))),
        );
      }),
    );
  },
  { functional: true },
);
